/**
 * Map a terminal outcome to the fill of its group marker (SE-6862).
 * @remarks
 * SIGNED is the one distinguished outcome — it is the result the pipeline
 * exists to produce, and the design marks it alone with the success fill. Every
 * other terminal outcome (passed, sold, promoted, returned, withdrawn) is
 * closed history of equal weight and shares the muted fill; ranking them
 * against each other by color would assert a hierarchy the product does not
 * have.
 *
 * The map is exhaustive over the enum rather than a lookup with a default, so
 * adding a terminal outcome to the schema is a COMPILE error here — the new
 * outcome's color becomes a decision someone has to make, instead of silently
 * inheriting the muted fill.
 * @module features/player-kanban/utils/outcomeDotColor
 */

import { KanbanCardOutcome } from "@/generated/graphql";

/** The distinguished outcome's fill. */
const SIGNED_DOT_CLASS = "bg-status-success";

/** Shared fill for every non-signed terminal outcome. */
const CLOSED_DOT_CLASS = "bg-content-muted";

/** Outcome → marker fill. Exhaustive by construction. */
const OUTCOME_DOT_CLASS: Record<KanbanCardOutcome, string> = {
  [KanbanCardOutcome.Signed]: SIGNED_DOT_CLASS,
  [KanbanCardOutcome.Passed]: CLOSED_DOT_CLASS,
  [KanbanCardOutcome.Sold]: CLOSED_DOT_CLASS,
  [KanbanCardOutcome.Promoted]: CLOSED_DOT_CLASS,
  [KanbanCardOutcome.Returned]: CLOSED_DOT_CLASS,
  [KanbanCardOutcome.Withdrawn]: CLOSED_DOT_CLASS,
};

/**
 * Resolve the marker fill for one terminal outcome.
 * @param outcome - The terminal outcome the group collects.
 * @returns The background token for that group's marker.
 */
export const outcomeDotClass = (outcome: KanbanCardOutcome): string =>
  OUTCOME_DOT_CLASS[outcome];
